import { memo, useCallback, useState } from 'react';
import { notification } from 'antd';
import { isAxiosError } from 'axios';

import type { ApiError, ApiErrorData } from '@/api/types';
import ModalButton from '@/ui/modal-button';
import {
  invalidateWorkerQualification,
  useCreateWorkerQualification,
  useUpdateWorkerQualification,
  useWorkerQualification,
} from '@src/entities/workers';
import { invalidateWorkersList } from '@src/entities/workers/queries';

import { WorkerQualificationForm } from '../../qualification-form';
import type { QualificationFormValues } from '../../qualification-form';

import css from './styles.module.scss';

interface WorkerModalProps {
  workerId: string;
  workerName: string;
  workerPhone?: string;
  workerInn?: string;
  workerContractor?: string;
  children: React.ReactNode;
}

export const WorkerModal = memo(
  ({
    workerId,
    workerName,
    workerPhone,
    workerInn,
    workerContractor,
    children,
  }: WorkerModalProps) => {
    const [modalOpen, setModalOpen] = useState(false);

    const { data: qualification, isFetching } = useWorkerQualification({
      params: { workerId },
      options: {
        enabled: modalOpen,
      },
    });

    const createMutation = useCreateWorkerQualification({
      onSuccess: (data) => {
        invalidateWorkerQualification(workerId);
        invalidateWorkersList();
        setModalOpen(false);
        notification.success({
          message: data?.data?.message || 'Данные сохранены',
          duration: 3,
        });
      },
      onError: (err) => {
        let text = 'Неизвестная ошибка, попробуйте позже';
        if (isAxiosError(err)) {
          const error = (err as ApiError).response?.data as ApiErrorData;
          text = error?.message ?? error?.error ?? text;
        }
        notification.error({
          message: text,
          duration: 5,
        });
      },
    });

    const updateMutation = useUpdateWorkerQualification({
      onSuccess: (data) => {
        invalidateWorkerQualification(workerId);
        invalidateWorkersList();
        setModalOpen(false);
        notification.success({
          message: data?.data?.message || 'Данные обновлены',
          duration: 3,
        });
      },
      onError: (err) => {
        let text = 'Неизвестная ошибка, попробуйте позже';
        if (isAxiosError(err)) {
          const error = (err as ApiError).response?.data as ApiErrorData;
          text = error?.message ?? error?.error ?? text;
        }
        notification.error({
          message: text,
          duration: 5,
        });
      },
    });

    const handleSubmit = useCallback(
      (data: QualificationFormValues) => {
        const quitAt = data.quitAt?.format('YYYY-MM-DD');

        if (qualification?.id) {
          updateMutation.mutate({
            workerId,
            brands: data.brands,
            quitAt,
            certification: data.certification,
          });
        } else {
          createMutation.mutate({
            workerId,
            brands: data.brands,
            quitAt,
            certification: data.certification,
          });
        }
      },
      [qualification?.id, workerId, createMutation, updateMutation],
    );

    const handleCancel = useCallback(() => {
      setModalOpen(false);
    }, []);

    const isLoading =
      isFetching || createMutation.isPending || updateMutation.isPending;

    return (
      <ModalButton
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        customButton={<>{children}</>}
        destroyOnClose
        width={650}
        className={css.modal}
        wrapClassName={css.modalWrap}
      >
        <WorkerQualificationForm
          workerId={workerId}
          workerName={workerName}
          workerPhone={workerPhone}
          workerInn={workerInn}
          workerContractor={workerContractor}
          initialData={qualification}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      </ModalButton>
    );
  },
);

WorkerModal.displayName = 'WorkerModal';

export default WorkerModal;
